import { useCallback, useRef } from 'react'

import { ApiError, api } from '../api/client.js'

/** A move, with how far into the round it was made. */
export interface Move {
  readonly action: unknown
  readonly at: number
}

export interface MoveLog {
  /** Tells the server about a move. Returns immediately; sending is its own business. */
  readonly record: (action: unknown, at: number) => void
  /**
   * Settles when everything recorded so far has reached the server.
   *
   * Resolves false if it could not get it all there, so a caller can decline to
   * ask for a score on a round the server has only half heard.
   */
  readonly flush: () => Promise<boolean>
}

/**
 * Posts moves to the server as they are made.
 *
 * The score is built from what the server was told, in the order it was told —
 * so this is not bookkeeping, it is the game. A round that used to arrive as
 * one payload at the end could leave its mistakes out of the telling and be
 * scored as though it had been played cleanly.
 *
 * Batched rather than one request per tap: a child answering ten questions
 * should not need ten round trips before she can finish, and one dropped
 * request should not cost a move. Each batch says where it believes it starts,
 * so a re-sent one is recognised rather than doubled.
 */
export function useMoveLog(roundId: string): MoveLog {
  /** Made, but not yet known to have arrived. */
  const pending = useRef<Move[]>([])
  /** How many the server has confirmed. Where the next batch says it starts. */
  const confirmed = useRef(0)
  /** One request at a time, so two batches cannot claim the same starting point. */
  const sending = useRef<Promise<void>>(Promise.resolve())

  /** Drops what the server has, and keeps the rest to send. */
  function advanceTo(held: number): void {
    const alreadyThere = Math.max(0, Math.min(pending.current.length, held - confirmed.current))

    pending.current = pending.current.slice(alreadyThere)
    confirmed.current = held
  }

  /**
   * Sends what is outstanding, once.
   *
   * Returns whether everything got through, so a caller that needs the server
   * to have heard it all — the end of a round — can try again rather than
   * assume.
   */
  const attempt = useCallback(async (): Promise<boolean> => {
    const batch = [...pending.current]
    if (batch.length === 0) return true

    try {
      const { held } = await api<{ held: number }>(`/api/rounds/${roundId}/moves`, {
        method: 'POST',
        body: JSON.stringify({ from: confirmed.current, moves: batch }),
      })

      advanceTo(held)
      return pending.current.length === 0
    } catch (error: unknown) {
      // A refusal usually knows more than we do.
      //
      // The dangerous case is a batch that arrived but whose reply was lost:
      // the server has moved on, we have not, and every later batch starts in
      // the wrong place. Re-sending harder never fixes that — it is the same
      // wrong batch — so the server says how many it holds and we start again
      // from there.
      const held = error instanceof ApiError ? error.body['held'] : undefined
      if (typeof held === 'number') {
        advanceTo(held)
        return pending.current.length === 0
      }

      // Nothing to learn from — offline, most likely. Everything stays where
      // it is for the next go.
      return false
    }
  }, [roundId])

  const send = useCallback((): Promise<void> => {
    sending.current = sending.current.then(async () => {
      await attempt()
    })

    return sending.current
  }, [attempt])

  return {
    record: useCallback(
      (action: unknown, at: number) => {
        pending.current.push({ action, at })
        void send()
      },
      [send],
    ),

    /**
     * Gets everything to the server, or gives up saying so.
     *
     * Tries more than once, because the end of a round is the moment it matters
     * most and a single blip should not cost the game. It does not try forever:
     * a child staring at a spinner is worse than a round that quietly did not
     * count.
     */
    flush: useCallback(async () => {
      for (let go = 0; go < FLUSH_ATTEMPTS; go += 1) {
        // One at a time, deliberately: each go depends on what the last one
        // learned from the server about where it had got to.
        const done = await send().then(() => pending.current.length === 0)
        if (done) return true
      }

      return pending.current.length === 0
    }, [send]),
  }
}

/** How many times a flush will try before letting the round go unrecorded. */
const FLUSH_ATTEMPTS = 3
